import React, { useMemo } from "react";
import { makeStyles, mergeClasses, Portal, shorthands, Spinner, tokens } from "@fluentui/react-components";
import { Button, Image } from "@fluentui/react-components";
import PropTypes from "prop-types";
import {
  ArrowShuffleFilled,
  CircleOffRegular,
  HandLeft28Filled,
  HandLeftFilled,
  PersonVoice24Filled
} from "@fluentui/react-icons";
import { ReactComponent as Avatar } from "./icons/avatars.svg";
import { ReactComponent as Head } from "./icons/head.svg";
import { ReactComponent as Hair } from "./icons/hair.svg";
import { ReactComponent as Hanger } from "./icons/hanger.svg";
import { ReactComponent as Glasses } from "./icons/glasses.svg";
import { ReactComponent as Facemask } from "./icons/facemask.svg";
import { ReactComponent as Facewear } from "./icons/facewear.svg";
import { ReactComponent as Headwear } from "./icons/cap.svg";
import { ReactComponent as Male } from "./icons/Male.svg";
import { ReactComponent as Female } from "./icons/Female.svg";
import { ReactComponent as BodyAverage } from "./icons/BodyAverage.svg";
import { ReactComponent as BodyAthletic } from "./icons/BodyAthletic.svg";
import { ReactComponent as BodyPlusSize } from "./icons/BodyPlussize.svg";
import { ReactComponent as Faceshape } from "./icons/Faceshape.svg";
import { ReactComponent as Eyeshape } from "./icons/Eyeshape.svg";
import { ReactComponent as Eyebrows } from "./icons/Eyebrows.svg";
import { ReactComponent as Noseshape } from "./icons/Noseshape.svg";
import { ReactComponent as Lipshape } from "./icons/Lipshape.svg";
import { ReactComponent as Beard } from "./icons/Beard.svg";
import { ReactComponent as Top } from "./icons/Top.svg";
import { ReactComponent as Bottom } from "./icons/Bottom.svg";
import { ReactComponent as Footwear } from "./icons/Footwear.svg";
import { ReactComponent as Outfit } from "./icons/Outfit.svg";
import { proxiedUrlFor } from "../../utils/media-url-utils";

const isAssetSelected = (equipped, current) => {
  if (Array.isArray(equipped) && Array.isArray(current)) {
    return equipped.length === current.length && equipped.every((val, index) => val === current[index]);
  }
  return equipped === current;
};

const getAvatarIconMaskStyle = iconUrl => ({
  "--avatar-category-icon": `url(${JSON.stringify(iconUrl)})`
});

export const mainCategories = [
  {
    id: "avatar",
    icon: <Avatar />,
    "aria-label": "Avatar",
    accessories: []
  },
  {
    id: "faceshape",
    icon: <Head />,
    "aria-label": "Face Shape",
    accessories: [
      {
        id: "faceshape",
        icon: <Faceshape />,
        "aria-label": "Face Shape"
      },
      {
        id: "eyeshape",
        icon: <Eyeshape />,
        "aria-label": "Eye Shape"
      },
      {
        id: "eyebrows",
        icon: <Eyebrows />,
        "aria-label": "Eyebrows"
      },
      {
        id: "noseshape",
        icon: <Noseshape />,
        "aria-label": "Nose Shape"
      },
      {
        id: "lipshape",
        icon: <Lipshape />,
        "aria-label": "Lip Shape"
      },
      {
        id: "beard",
        icon: <Beard />,
        "aria-label": "Beard"
      }
    ]
  },
  {
    id: "hair",
    icon: <Hair />,
    "aria-label": "Hair",
    accessories: [
      {
        id: "hair",
        icon: <Hair />,
        "aria-label": "Hair"
      }
    ]
  },
  {
    id: "outfit",
    icon: <Hanger />,
    "aria-label": "Outfit",
    accessories: [
      {
        id: "top",
        icon: <Top />,
        "aria-label": "Top"
      },
      {
        id: "bottom",
        icon: <Bottom />,
        "aria-label": "Bottom"
      },
      {
        id: "footwear",
        icon: <Footwear />,
        "aria-label": "Footwear"
      },
      {
        id: "outfit",
        type: "overall",
        icon: <Outfit />,
        "aria-label": "Outfit"
      }
    ]
  },

  {
    id: "facemask",
    icon: <Facemask />,
    "aria-label": "Facemask",
    accessories: [
      {
        id: "facemask",
        icon: <Facemask />,
        "aria-label": "Facemask"
      }
    ]
  },
  {
    id: "facewear",
    icon: <Facewear />,
    "aria-label": "Facewear",
    accessories: [
      {
        id: "facewear",
        icon: <Facewear />,
        "aria-label": "Facewear"
      }
    ]
  },
  {
    id: "headwear",
    icon: <Headwear />,
    "aria-label": "Headwear",
    accessories: [
      {
        id: "headwear",
        icon: <Headwear />,
        "aria-label": "Headwear"
      }
    ]
  },
  // Stylized-unique categories (may be empty for humanoid avatars):
  {
    id: "hands",
    icon: <HandLeft28Filled />,
    "aria-label": "Hands",
    stylizedOnly: true,
    accessories: [
      {
        id: "hands",
        icon: <HandLeft28Filled />,
        "aria-label": "Hands"
      }
    ]
  },
  {
    id: "mouth",
    icon: <Lipshape />,
    "aria-label": "Mouth",
    stylizedOnly: true,
    accessories: [
      {
        id: "mouth",
        icon: <Lipshape />,
        "aria-label": "Mouth"
      }
    ]
  },
  {
    id: "eyewear",
    icon: <Glasses />,
    "aria-label": "Eyewear",
    stylizedOnly: true,
    accessories: [
      {
        id: "eyewear",
        icon: <Glasses />,
        "aria-label": "Eyewear"
      }
    ]
  },
  {
    id: "earrings",
    icon: <PersonVoice24Filled />,
    "aria-label": "Earrings",
    stylizedOnly: true,
    accessories: [
      {
        id: "earrings",
        icon: <PersonVoice24Filled />,
        "aria-label": "Earrings"
      }
    ]
  },
  {
    id: "accessories",
    icon: <Facemask />,
    "aria-label": "Accessories",
    stylizedOnly: true,
    accessories: [
      {
        id: "accessories",
        icon: <Facemask />,
        "aria-label": "Accessories"
      }
    ]
  }
];

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontFamily: tokens.fontFamilyBase,
    height: "100%"
  },
  title: {
    marginBottom: "20px",
    color: tokens.colorNeutralForeground1
  },
  mainLayout: {
    display: "flex",
    gap: tokens.spacingHorizontalNone,
    height: "100%",
    flexDirection: "column",
    "@media (min-width: 992px)": {
      flexDirection: "row"
    }
  },
  itemGridPanel: {
    width: "100%",
    paddingRight: "10px",
    overflow: "auto",
    minHeight: "50px"
  },
  itemGridContent: {
    display: "flex",
    gap: tokens.spacingHorizontalL,
    padding: tokens.spacingVerticalS,
    // justifyContent: "space-around"
    "@media (min-width: 992px)": {
      flexWrap: "wrap",
      padding: 0
    }
  },
  gridItemButton: {
    ...shorthands.padding("0px"),
    ...shorthands.border("1px", "solid", "transparent"),
    borderRadius: tokens.borderRadiusMedium,
    height: "50px",
    minWidth: "50px",
    width: "50px",
    backgroundColor: "transparent",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover
    },
    "@media (min-width: 992px)": {
      height: "85px",
      minWidth: "85px",
      width: "85px",
      minHeight: "85px"
    }
  },
  gridItemButtonSelected: {
    ...shorthands.border("2px", "solid", "var(--primary-color)"),
    backgroundColor: "var(--primaryColorBackground)"
  },
  gridItemImage: {
    width: "100%",
    height: "100%",
    display: "block"
  },
  avatarIconMask: {
    width: "100%",
    height: "100%",
    display: "block",
    backgroundColor: "currentColor",
    maskImage: "var(--avatar-category-icon)",
    maskPosition: "center",
    maskRepeat: "no-repeat",
    maskSize: "contain",
    WebkitMaskImage: "var(--avatar-category-icon)",
    WebkitMaskPosition: "center",
    WebkitMaskRepeat: "no-repeat",
    WebkitMaskSize: "contain"
  },
  bodyShapeIcon: {
    width: "36px",
    height: "36px",
    userSelect: "none"
  },
  color: {
    width: "100%",
    height: "100%",
    borderRadius: tokens.borderRadiusCircular
  },
  categoryPanel: {
    display: "flex",
    overflowY: "auto",
    // justifyContent: "center", // The objects get out of the screen if this is set
    gap: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: "20px",
    paddingRight: "20px",
    "@media (min-width: 992px)": {
      flexDirection: "column",
      justifyContent: "flex-start",
      width: "105px",
      minWidth: "105px",
      paddingRight: tokens.spacingVerticalMNudge,
      paddingLeft: tokens.spacingVerticalMNudge,
      paddingTop: tokens.spacingVerticalM,
      paddingBottom: tokens.spacingVerticalM
    }
  },
  middleCategoryPanel: {
    backgroundColor: tokens.colorNeutralBackground3,
    justifyContent: "center"
  },
  rightCategoryPanel: {
    backgroundColor: tokens.colorNeutralBackground4
  },
  // Style for individual category icon buttons
  categoryButton: {
    width: "50px",
    minWidth: "50px",
    height: "50px",
    "@media (min-width: 992px)": {
      minWidth: "84px",
      height: "84px",
      width: "100%",
      "& svg": {
        fontSize: "84px",
        width: "84px",
        height: "84px"
      }
    },
    maxWidth: "unset",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    ...shorthands.padding(0),
    display: "flex", // For centering the icon inside the button
    alignItems: "center",
    justifyContent: "center",
    ":hover path": {
      fill: "var(--primaryColor)"
    },
    "& .fui-Button__icon": {
      width: "100%",
      height: "100%"
    },
    "& svg": {
      // Styling the SVG icon itself
      fontSize: "50px",
      width: "50px",
      height: "50px"
    },
    // Margin left auto for the first button in the category panel
    "&:first-child": {
      marginLeft: "auto"
    },
    // marginRight: "auto" for the last button in the category panel
    "&:last-child": {
      marginRight: "auto"
    }
  },
  categoryButtonSelectedMiddle: {
    backgroundColor: tokens.colorNeutralBackground4,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground4
    },
    "& path": {
      fill: "var(--primary-color)"
    }
  },
  categoryButtonSelectedRight: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: "var(--primaryColor)",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1
    },
    "& path": {
      fill: "var(--primary-color)"
    }
  },
  avatarButtons: {
    display: "flex",
    padding: "10px 20px",
    flexDirection: "column-reverse",
    gap: "10px",
    "@media (min-width: 992px)": {
      flexDirection: "column",
      gap: "20px",
      padding: "0 20px"
    }
  },
  avatarTypeButtonsContainer: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "10px",
    "@media (min-width: 992px)": {
      flexDirection: "column",
      justifyContent: "flex-start",
      gap: "20px"
    }
  },
  avatarTypeButtons: {
    display: "flex",
    gap: "5px",
    "@media (min-width: 992px)": {
      gap: "10px"
    },
    "& > button": {
      width: "50px",
      minWidth: "unset",
      height: "50px",
      "@media (min-width: 992px)": {
        height: "70px",
        width: "100%"
      }
    }
  },
  helperPanel: {
    display: "flex",
    overflow: "auto",
    position: "fixed",
    margin: "0 auto",
    top: "52px",
    left: "0",
    backgroundColor: tokens.colorNeutralBackground1,
    zIndex: 10,
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    width: "100%",
    "@media (min-width: 992px)": {
      width: "auto",
      padding: tokens.spacingVerticalL,
      height: "70dvh",
      flexDirection: "column",
      top: "50%",
      transform: "translateY(-50%)",
      left: "50px",
      borderRadius: tokens.borderRadiusMedium
    }
  },
  helperItemButton: {
    width: "50px",
    height: "50px",
    minWidth: "50px",
    minHeight: "50px",

    "@media (min-width: 992px)": {
      minHeight: "60px",
      minWidth: "60px",
      width: "60px",
      height: "60px"
    }
  },
  categoryButtonContainer: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    width: "100%"
  },
  removeButton: {
    minWidth: "84px",
    width: "84px",
    height: "84px",
    ...shorthands.padding(0),
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3Hover
    }
  },
  selectButton: {
    width: "50px",
    height: "50px",
    "@media (min-width: 992px)": {
      width: "100%",
      height: "70px"
    },
    minWidth: "50px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    border: "1px solid transparent",
    backgroundColor: "transparent",
    cursor: "pointer"
  },
  selectButtonSelected: {
    backgroundColor: "var(--primaryColorBackground)",
    border: "1px solid var(--primary-color)",
    color: "var(--primary-color)",
    "& svg": {
      fill: "var(--primary-color)"
    }
  },
  categoryButtonDisabled: {
    opacity: 0.3,
    cursor: "not-allowed",
    pointerEvents: "none"
  },
  assetPlaceholder: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    fontSize: "10px",
    fontWeight: "500",
    color: tokens.colorNeutralForeground1,
    textAlign: "center",
    padding: "4px",
    wordBreak: "break-word",
    lineHeight: 1.2
  }
});

export const AvatarHangerAssets = ({
  assets,
  handleEquipAsset,
  handleAvatarTypeChange,
  equippedAssets,
  onRandomizeAvatar,
  avatarType = "male",
  selectedCategory: selectedAccessory,
  onCategoryChange = () => {},
  colors = {},
  selectedMainCategory,
  onMainCategoryChange,
  loading,
  isMobile = false,
  avatarEditorRef,
  disabledCategories = [],
  isAvatarBuilderMode = false,
  categories = []
}) => {
  const styles = useStyles();

  // Use dynamic categories if available, otherwise fallback to hardcoded
  const visibleMainCategories = useMemo(() => {
    if (categories && categories.length > 0) {
      return categories;
    }
    return mainCategories;
  }, [categories]);

  const accessories = useMemo(() => {
    const category = visibleMainCategories.find(cat => cat.id === selectedMainCategory);
    return category?.subcategories || category?.accessories || [];
  }, [selectedMainCategory, visibleMainCategories]);

  const items = useMemo(() => {
    return assets[selectedAccessory] || [];
  }, [assets, selectedAccessory]);

  // Extract it from the equipped assets
  const bodyShape = useMemo(() => {
    return equippedAssets?.bodyShape || "average";
  }, [equippedAssets]);

  const selectedAccessoryType =
    accessories.find(category => category.id === selectedAccessory)?.type || selectedAccessory;

  const eyeColorAssets = useMemo(() => {
    if (selectedAccessory === "eyeshape") {
      return assets.eye || [];
    }
    return [];
  }, [assets, selectedAccessory]);

  const visuallyEquippedAssets = useMemo(() => {
    if (equippedAssets.overall) {
      const rest = { ...equippedAssets };
      delete rest.top;
      delete rest.bottom;
      delete rest.footwear;
      return rest;
    }
    return equippedAssets;
  }, [equippedAssets]);

  return (
    <div className={styles.root}>
      <div className={styles.mainLayout}>
        <div className={styles.itemGridPanel}>
          {selectedMainCategory === "avatar" && (
            <div className={styles.avatarButtons}>
              {/* Only show body shape controls if this avatar type supports body morphs */}
              {assets.features?.bodyMorphs !== false && (
                <div className={styles.avatarTypeButtonsContainer}>
                  <div className={styles.avatarTypeButtons}>
                    {/* Use dynamic presets from backend, fallback to hardcoded */}
                    {(assets.bodyShapePresets?.length > 0
                      ? assets.bodyShapePresets.map(preset => ({
                          key: preset.id,
                          icon: preset.icon,
                          displayName: preset.displayName
                        }))
                      : [
                          { key: "average", icon: null, displayName: "Average" },
                          { key: "athletic", icon: null, displayName: "Athletic" },
                          { key: "plussize", icon: null, displayName: "Plus Size" }
                        ]
                    ).map(shape => {
                      // Fallback icons for backwards compatibility
                      const fallbackIcons = {
                        average: <BodyAverage width="24" height="24" />,
                        athletic: <BodyAthletic width="24" height="24" />,
                        plussize: <BodyPlusSize width="24" height="24" />
                      };
                      const iconElement =
                        typeof shape.icon === "string" ? (
                          <span
                            aria-hidden="true"
                            className={mergeClasses(styles.avatarIconMask, styles.bodyShapeIcon)}
                            style={getAvatarIconMaskStyle(proxiedUrlFor(shape.icon))}
                          />
                        ) : (
                          fallbackIcons[shape.key] || <BodyAverage width="24" height="24" />
                        );

                      return (
                        <div
                          key={shape.key}
                          size="large"
                          role="button"
                          appearance={bodyShape === shape.key ? "primary" : "transparent"}
                          onClick={() => handleEquipAsset("bodyShape", shape.key)}
                          className={mergeClasses(
                            styles.selectButton,
                            bodyShape === shape.key && styles.selectButtonSelected
                          )}
                        >
                          {iconElement}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <Button
                appearance="primary"
                onClick={onRandomizeAvatar}
                style={{
                  width: isMobile ? "50px" : "100%",
                  height: isMobile ? "50px" : "auto",
                  minWidth: "50px"
                }}
                icon={<ArrowShuffleFilled />}
                size={isMobile ? "large" : ""}
              >
                {!isMobile && <>Random Avatar</>}
              </Button>
            </div>
          )}
          {selectedMainCategory !== "avatar" && (
            <div className={styles.itemGridContent}>
              {loading === "category" ? (
                <Spinner appearance="primary" size="large" style={{ margin: "auto" }} />
              ) : (
                <>
                  {[
                    ...(selectedMainCategory !== "outfit"
                      ? [
                          {
                            id: "",
                            icon: <CircleOffRegular className={styles.gridItemButton} />,
                            alt: `Remove ${selectedAccessory}`,
                            onClick: () => handleEquipAsset(selectedAccessoryType, ""),
                            isRemoveButton: true
                          }
                        ]
                      : []),
                    ...items
                  ].map(item => (
                    <Button
                      key={item.id}
                      appearance="transparent"
                      className={mergeClasses(
                        styles.gridItemButton,
                        visuallyEquippedAssets[selectedAccessoryType] === item.id && styles.gridItemButtonSelected
                      )}
                      onClick={() => {
                        handleEquipAsset(selectedAccessoryType, item.id);
                      }}
                      aria-pressed={visuallyEquippedAssets[selectedAccessoryType] === item.id}
                      aria-label={item.alt}
                    >
                      {item.isRemoveButton ? (
                        item.icon
                      ) : item.iconUrl ? (
                        <Image
                          src={proxiedUrlFor(item.iconUrl)}
                          alt={item.alt || item.displayName}
                          className={styles.gridItemImage}
                        />
                      ) : (
                        <div className={styles.assetPlaceholder}>{item.displayName || item.id}</div>
                      )}
                    </Button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {accessories?.length > 1 && (
          <div className={mergeClasses(styles.categoryPanel, styles.middleCategoryPanel)}>
            {accessories.map(category => {
              const isSubcategoryDisabled =
                isAvatarBuilderMode &&
                selectedMainCategory === "faceshape" &&
                category.id !== "eyeshape" &&
                category.id !== "eyes" &&
                category.id !== "faceshape";
              return (
                <Button
                  key={category.id}
                  appearance="transparent"
                  icon={
                    typeof category.icon === "string" ? (
                      <span
                        aria-hidden="true"
                        className={styles.avatarIconMask}
                        style={getAvatarIconMaskStyle(proxiedUrlFor(category.icon))}
                      />
                    ) : (
                      category.icon
                    )
                  }
                  aria-label={category["aria-label"] || category.label}
                  disabled={isSubcategoryDisabled}
                  className={mergeClasses(
                    styles.categoryButton,
                    selectedAccessory === category.id && styles.categoryButtonSelectedMiddle,
                    isSubcategoryDisabled && styles.categoryButtonDisabled
                  )}
                  onClick={() => onCategoryChange(category.id)}
                  aria-pressed={selectedAccessory === category.id}
                />
              );
            })}
          </div>
        )}

        <div className={mergeClasses(styles.categoryPanel, styles.rightCategoryPanel)}>
          {visibleMainCategories.map(category => {
            const isDisabled = disabledCategories.includes(category.id);
            return (
              <Button
                key={category.id}
                appearance="transparent"
                icon={
                  typeof category.icon === "string" ? (
                    <span
                      aria-hidden="true"
                      className={styles.avatarIconMask}
                      style={getAvatarIconMaskStyle(proxiedUrlFor(category.icon))}
                    />
                  ) : (
                    category.icon
                  )
                }
                aria-label={category["aria-label"] || category.label}
                disabled={isDisabled}
                className={mergeClasses(
                  styles.categoryButton,
                  selectedMainCategory === category.id && styles.categoryButtonSelectedRight,
                  isDisabled && styles.categoryButtonDisabled
                )}
                onClick={() => !isDisabled && onMainCategoryChange(category.id)}
                aria-pressed={selectedMainCategory === category.id}
              />
            );
          })}
        </div>
      </div>

      <Portal mountNode={avatarEditorRef}>
        <>
          {colors[selectedAccessory] && (
            <div className={styles.helperPanel}>
              {colors[selectedAccessory].map((color, index) => {
                const equippedColor = visuallyEquippedAssets[color.type];
                const isSelected =
                  isAssetSelected(equippedColor, color.id) ||
                  ((equippedColor === null || equippedColor === undefined) &&
                    index === 0);
                return (
                  <Button
                    key={`${color.type}-${color.id}`}
                    appearance="transparent"
                    className={mergeClasses(
                      styles.gridItemButton,
                      styles.helperItemButton,
                      isSelected && styles.gridItemButtonSelected
                    )}
                    onClick={() => handleEquipAsset(color.type, color.id)}
                    aria-pressed={isSelected}
                    aria-label={color.label}
                  >
                    {color.iconUrl ? (
                      <Image
                        src={proxiedUrlFor(color.iconUrl)}
                        alt={color.label}
                        className={styles.gridItemImage}
                      />
                    ) : (
                      <div
                        className={styles.color}
                        style={{ backgroundColor: color.color }}
                      />
                    )}
                  </Button>
                );
              })}
            </div>
          )}

          {eyeColorAssets.length > 0 && (
            <div className={styles.helperPanel}>
              {eyeColorAssets.map((eye, index) => {
                const equippedEye = visuallyEquippedAssets.eyeColor;
                const isSelected =
                  isAssetSelected(equippedEye, eye.id) ||
                  ((equippedEye === null || equippedEye === undefined) &&
                    index === 0);
                return (
                  <Button
                    key={eye.id}
                    appearance="transparent"
                    className={mergeClasses(
                      styles.gridItemButton,
                      styles.helperItemButton,
                      isSelected && styles.gridItemButtonSelected
                    )}
                    onClick={() => handleEquipAsset("eyeColor", eye.id)}
                    aria-pressed={isSelected}
                    aria-label={eye.label}
                  >
                    <Image
                      src={proxiedUrlFor(eye.iconUrl)}
                      alt={eye.label}
                      className={styles.gridItemImage}
                    />
                  </Button>
                );
              })}
            </div>
          )}
        </>
      </Portal>
    </div>
  );
};

AvatarHangerAssets.propTypes = {
  assets: PropTypes.object,
  handleEquipAsset: PropTypes.func,
  handleAvatarTypeChange: PropTypes.func,
  equippedAssets: PropTypes.object,
  onRandomizeAvatar: PropTypes.func,
  avatarType: PropTypes.string,
  selectedCategory: PropTypes.string,
  onCategoryChange: PropTypes.func,
  colors: PropTypes.object,
  selectedMainCategory: PropTypes.string,
  onMainCategoryChange: PropTypes.func,
  loading: PropTypes.string,
  isMobile: PropTypes.bool,
  avatarEditorRef: PropTypes.object,
  disabledCategories: PropTypes.array,
  isAvatarBuilderMode: PropTypes.bool,
  categories: PropTypes.array
};
